
import * as React from 'react';
import { IItemCache } from '../../models/IItemCacheObserver';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IRenderProps, renderChildrenWithProps } from '../../utilities/components/RenderProps';
import { IReactChildrenProps } from '../../utilities/components/Props';

/**
 * The props passed to the callback provided as children to the WithItemCache component.
 * @public
 */
export interface IItemCacheStateProps {
    /**
     * An accessor for the state of the current item cache.
     * If the render callback demands and items and facets from the instance of the cache,
     * the WithItemCache component will subscribe to the state of those items and
     * invoke the callback every time their states change.
     */
    itemCache: IItemCache;
    /**
     * The dispatcher for the item cache store.
     * Use this to bind actions which should update the item cache state.
     */
    dispatch: IItemCacheDispatch;
}

/**
 * The props passed to a WithItemCache component.
 * @public
 */
export interface IWithItemCacheProps extends IRenderProps<IItemCacheStateProps> {
    // Nothing additional.
}

/**
 * Component which provides an accessor and dispatcher for the current item cache state.
 *
 * Render this component and pass a callback function as the only child.
 * The callback will be passed the item cache and the dispatch function from the current item cache,
 * and should render React elements.
 * @public
 */
export const WithItemCache: React.ComponentType<IWithItemCacheProps> = connectToItemCache(
    mapItemCacheToElement,
    unbindItemCacheDispatchProp
)(React.Fragment);

WithItemCache.displayName = 'WithItemCache';

function mapItemCacheToElement(
    itemCache: IItemCache,
    props: IWithItemCacheProps
): IReactChildrenProps {
    const {
        children
    } = props;

    const { dispatch } = itemCache;

    return {
        children: renderChildrenWithProps(children, {
            itemCache,
            dispatch
        })
    };
}

function unbindItemCacheDispatchProp(): {} {
    return {};
}
